// bucketTransforms.js

// Utilities
import { isVoiceVideoChannel } from 'channels/constants/channelTypes';
import { getPluralLabel } from 'utilities/Languages';

// Shared Value Normalizer
export const normalizeBucketValue = (value) => String(value || '').trim();

const SIDEBAR_SYSTEM_DATABASE_TYPES = new Set(['PHOTO', 'VIDEO', 'DOCUMENT', 'FILE', 'ARTICLE', 'NOTE']);

const normalizeTypeKeyUpper = (value) => normalizeBucketValue(value).toUpperCase();

const isPinnedSystemDatabaseType = ({ typeKey, def }) => {
    const normalizedTypeKey = normalizeTypeKeyUpper(typeKey);
    if (!SIDEBAR_SYSTEM_DATABASE_TYPES.has(normalizedTypeKey)) return false;
    if (!def) return true;

    const definitionTypeKey = normalizeTypeKeyUpper(def?.key || def?.primitiveKey || normalizedTypeKey);
    return SIDEBAR_SYSTEM_DATABASE_TYPES.has(definitionTypeKey);
};

const isWorkspaceScopedTypeDef = ({ def, workspaceKey }) => {
    const typeWorkspaceKey = normalizeBucketValue(def?.workspaceKey);
    const activeWorkspaceKey = normalizeBucketValue(workspaceKey);
    if (!activeWorkspaceKey) return Boolean(typeWorkspaceKey);
    return typeWorkspaceKey === activeWorkspaceKey;
};

const canViewerSeeDashboard = ({ dashboard, viewerUserKey }) => {
    const isPrivate = dashboard?.private === true;
    if (!isPrivate) return true;

    const creatorKey = normalizeBucketValue(dashboard?.createdBy);
    if (!creatorKey) return true;

    return creatorKey === normalizeBucketValue(viewerUserKey);
};

// Workspace Database Rows
export const buildWorkspaceDatabases = ({ databases, getType, language, workspaceKey }) => {
    return (Array.isArray(databases) ? databases : [])
        .filter((database) => {
            const moduleKey = normalizeBucketValue(database?.moduleKey);
            if (moduleKey) return false;
            if (database?.importedByModule === true) return false;

            const typeKey = normalizeBucketValue(database?.type);
            if (!typeKey) return true;

            const def = getType(typeKey);
            // Keep rows visible even if the type cache has not hydrated yet.
            // The database list is already workspace-scoped, so optimistic inclusion is safe.
            if (!def) return true;
            if (isWorkspaceScopedTypeDef({ def, workspaceKey })) return true;

            return isPinnedSystemDatabaseType({ typeKey, def });
        })
        .map((database) => {
            const typeKey = database?.type || '';
            const def = typeKey ? getType(typeKey) : null;
            const rawTitle = database?.title || '';
            const databaseKey = normalizeBucketValue(database?.key);
            // Some legacy/default databases persist title=typeKey placeholder.
            // Treat that as unresolved and prefer the node type plural label.
            const isTypeKeyPlaceholderTitle = normalizeBucketValue(rawTitle) === normalizeBucketValue(typeKey);
            const displayTitle = rawTitle && rawTitle !== databaseKey && !isTypeKeyPlaceholderTitle
                ? rawTitle
                : (typeKey ? getPluralLabel(def, typeKey, language) : 'Untitled');

            const rawIconKey = typeof def?.iconKey === 'string' ? def.iconKey.trim() : '';

            return {
                key: databaseKey,
                iconKey: rawIconKey || 'deployed_code',
                label: displayTitle,
                database,
            };
        })
        .filter((row) => row.key)
        .sort((a, b) => a.label.localeCompare(b.label));
};

// Module Database Rows
export const buildModuleDatabaseRows = ({ modules, databases, getType, language, workspaceKey }) => {
    const grouped = new Map();

    (Array.isArray(modules) ? modules : []).forEach((row) => {
        const key = normalizeBucketValue(row?.key);
        if (!key) return;
        grouped.set(key, []);
    });

    (Array.isArray(databases) ? databases : []).forEach((database) => {
        const moduleKey = normalizeBucketValue(database?.moduleKey);
        if (!moduleKey || !grouped.has(moduleKey)) return;

        const typeKey = database?.type || '';
        const def = typeKey ? getType(typeKey) : null;
        if (typeKey && def && !isWorkspaceScopedTypeDef({ def, workspaceKey })) {
            if (!isPinnedSystemDatabaseType({ typeKey, def })) return;
        }

        const rawTitle = database?.title || '';
        const databaseKey = normalizeBucketValue(database?.key);
        // Some legacy/default databases persist title=typeKey placeholder.
        // Treat that as unresolved and prefer the node type plural label.
        const isTypeKeyPlaceholderTitle = normalizeBucketValue(rawTitle) === normalizeBucketValue(typeKey);
        const displayTitle = rawTitle && rawTitle !== databaseKey && !isTypeKeyPlaceholderTitle
            ? rawTitle
            : (typeKey ? getPluralLabel(def, typeKey, language) : 'Untitled');

        const rawIconKey = typeof def?.iconKey === 'string' ? def.iconKey.trim() : '';

        grouped.get(moduleKey).push({
            key: databaseKey,
            iconKey: rawIconKey || 'deployed_code',
            label: displayTitle,
            database,
        });
    });

    grouped.forEach((rows, key) => {
        grouped.set(key, rows.filter((row) => row.key).sort((a, b) => a.label.localeCompare(b.label)));
    });

    return grouped;
};

// Workspace Dashboard Rows
export const buildWorkspaceDashboards = ({ dashboards, viewerUserKey, pinnedDashboardKey }) => {
    const normalizedPinnedDashboardKey = normalizeBucketValue(pinnedDashboardKey);

    return (Array.isArray(dashboards) ? dashboards : [])
        .filter((row) => !normalizeBucketValue(row?.moduleKey))
        .filter((row) => canViewerSeeDashboard({ dashboard: row, viewerUserKey }))
        .map((row) => {
            const key = normalizeBucketValue(row?.key);

            return {
                key,
                title: row?.title || 'Untitled Dashboard',
                iconKey: typeof row?.iconKey === 'string' && row.iconKey.trim() ? row.iconKey.trim() : 'dashboard',
                private: row?.private === true,
                pinned: Boolean(normalizedPinnedDashboardKey && key === normalizedPinnedDashboardKey),
            };
        })
        .filter((row) => row.key)
        .sort((a, b) => a.title.localeCompare(b.title));
};

// Module Dashboard Rows
export const buildModuleDashboardRows = ({ modules, dashboards, viewerUserKey, pinnedDashboardKey }) => {
    const normalizedPinnedDashboardKey = normalizeBucketValue(pinnedDashboardKey);
    const grouped = new Map();

    (Array.isArray(modules) ? modules : []).forEach((row) => {
        const key = normalizeBucketValue(row?.key);
        if (!key) return;
        grouped.set(key, []);
    });

    (Array.isArray(dashboards) ? dashboards : []).forEach((dashboard) => {
        const moduleKey = normalizeBucketValue(dashboard?.moduleKey);
        if (!moduleKey || !grouped.has(moduleKey)) return;
        if (!canViewerSeeDashboard({ dashboard, viewerUserKey })) return;

        grouped.get(moduleKey).push({
            key: normalizeBucketValue(dashboard?.key),
            title: dashboard?.title || 'Untitled Dashboard',
            iconKey: typeof dashboard?.iconKey === 'string' && dashboard.iconKey.trim()
                ? dashboard.iconKey.trim()
                : 'dashboard',
            private: dashboard?.private === true,
            pinned: Boolean(
                normalizedPinnedDashboardKey
                && normalizeBucketValue(dashboard?.key) === normalizedPinnedDashboardKey
            ),
        });
    });

    grouped.forEach((rows, key) => {
        grouped.set(key, rows.filter((row) => row.key).sort((a, b) => a.title.localeCompare(b.title)));
    });

    return grouped;
};

// Workspace Channel Rows
export const buildWorkspaceChannels = ({ channels }) => {
    return (Array.isArray(channels) ? channels : [])
        .filter((row) => !normalizeBucketValue(row?.moduleKey))
        .map((row) => ({
            key: normalizeBucketValue(row?.key),
            title: row?.title || 'Untitled Channel',
            iconKey: isVoiceVideoChannel(row?.channelType) ? 'video_camera_front' : 'tag',
        }))
        .filter((row) => row.key)
        .sort((a, b) => a.title.localeCompare(b.title));
};

// Workspace Page Rows
export const buildWorkspacePages = ({ pages }) => {
    return (Array.isArray(pages) ? pages : [])
        .map((page) => ({
            key: normalizeBucketValue(page?.key),
            title: page?.title || 'Untitled Page',
            iconKey: 'article',
            status: normalizeBucketValue(page?.status || 'draft').toLowerCase(),
        }))
        .filter((row) => row.key)
        .sort((a, b) => a.title.localeCompare(b.title));
};

// Module Channel Rows
export const buildModuleChannelRows = ({ modules, channels }) => {
    const grouped = new Map();

    (Array.isArray(modules) ? modules : []).forEach((row) => {
        const key = normalizeBucketValue(row?.key);
        if (!key) return;
        grouped.set(key, []);
    });

    (Array.isArray(channels) ? channels : []).forEach((channel) => {
        const moduleKey = normalizeBucketValue(channel?.moduleKey);
        if (!moduleKey || !grouped.has(moduleKey)) return;

        grouped.get(moduleKey).push({
            key: normalizeBucketValue(channel?.key),
            title: channel?.title || 'Untitled Channel',
            iconKey: isVoiceVideoChannel(channel?.channelType) ? 'video_camera_front' : 'tag',
        });
    });

    grouped.forEach((rows, key) => {
        grouped.set(key, rows.filter((row) => row.key).sort((a, b) => a.title.localeCompare(b.title)));
    });

    return grouped;
};

// Composed Buckets
export const composeBuckets = ({
    modules,
    moduleDatabaseRows,
    moduleDashboardRows,
    moduleChannelRows,
    workspaceDatabases,
    workspaceDashboards,
    workspaceChannels,
}) => {
    const moduleBuckets = (Array.isArray(modules) ? modules : [])
        .map((moduleRow) => {
            const moduleKey = normalizeBucketValue(moduleRow?.key);
            if (!moduleKey) return null;

            return {
                key: `module:${moduleKey}`,
                title: moduleRow?.title || 'Untitled Template',
                dashboards: moduleDashboardRows.get(moduleKey) || [],
                databases: moduleDatabaseRows.get(moduleKey) || [],
                channels: moduleChannelRows.get(moduleKey) || [],
                moduleKey,
            };
        })
        .filter(Boolean);

    return [
        {
            key: 'workspace',
            title: 'Workspace',
            dashboards: workspaceDashboards,
            databases: workspaceDatabases,
            channels: workspaceChannels,
            moduleKey: null,
        },
        ...moduleBuckets,
    ];
};